// @flow
import type { Saga } from 'redux-saga';
import { call, put, takeLatest, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import jwtDecode from 'jwt-decode';
import { toast } from 'react-toastify';

import { getUserCode } from '../selectors';
import { User, setAccessToken, clearAccessToken } from '../../api';
import authActions from './actions';
import { loginTypes, userTypes, impersonateTypes } from './types';
import { encodePassword, validatePassword, SECURITY_LIMIT } from '../../helpers';

export function* logoutSaga({ payload }: { payload: string }): Saga {
  try {
    yield call(clearAccessToken);
    yield put(authActions.logoutSuccess(payload));
  } catch (error) {
    yield put(authActions.logoutFailed(`Ocurrió un error al intentar borrar el access token. ${error}`));
  }
}

export function* loginRequestSaga({ payload }: { payload: Object }): Saga {
  try {
    if (!payload.token) {
      yield put(authActions.loginFailed('Fallo de seguridad, recargue la página y vuelva a intentarlo'));
      return;
    }

    const dataCaptcha = yield call(User.validateToken, {
      token: payload.token
    });

    if (process.env.REACT_APP_ENV === "prodint" || dataCaptcha.success && dataCaptcha.score >= SECURITY_LIMIT) {
      const data = yield call(User.login, {
        userName: payload.userName,
        passEncoded: encodePassword(payload.password)
      });
      if (data.success) {
        const { user, token } = data;
        yield call(setAccessToken, token);
        const isPasswordSecure = yield call(validatePassword, payload.password);
        yield put(authActions.setPasswordSecurity(isPasswordSecure));
        yield put(authActions.loginSuccess(user));
        if (user.isAdmin) {
          yield put(push('/admin/pagos'));
        } else {
          yield put(push('/ordenes'));
        }
      } else {
        yield put(authActions.loginFailed(data.message));
      }
    } else {
      yield put(authActions.loginFailed('Fallo en validación de seguridad, vuelva a intentarlo mas tarde'));
    }
  } catch (error) {
    yield put(authActions.loginFailed('Ocurrió un error al intentar ingresar.'));
  }
}

// SAGA para las acciones apenas re hidrata el State.
export function* rehydrateSaga(): Saga {
  try {
    const token = localStorage.getItem('ocot_access_token');
    const decoded = jwtDecode(token);
    const currentTime = new Date().getTime() / 1000;
    if (currentTime > decoded.exp) {
      /* expired */
      yield put(authActions.logout('Expiró la sesión'));
    }
  } catch (error) {
    // console.log('Ocurrió un error al intentar obtener los datos cliente.');
  }
}
export function* acceptTermsRequest({ payload }: { payload: Object }) {
  try {
    const { success = false } = yield call(User.acceptTerms, {
      userCode: payload.userCode
    });

    if (success) {
      yield put(authActions.acceptTermsSuccess());
      yield call(toast.success, 'Has aceptado los terminos y condiciones.');
    } else {
      yield put(authActions.acceptTermsFailed());
      yield call(toast.error, 'Ha ocurrido un error.');
    }
  } catch (e) {
    yield put(authActions.acceptTermsFailed());
    yield call(toast.error, 'Ha ocurrido un error.');
  }
}
export function* changePasswordRequest({ payload }: { payload: Object }) {
  try {
    const { 
      success = false,
      message = ''
    } = yield call(User.changePassword, {
      userName: yield select(getUserCode), 
      oldPassEncoded: encodePassword(payload.oldPassword), 
      newPassEncoded: encodePassword(payload.newPassword),
    });

    if (success) {
      const isPasswordSecure = yield call(validatePassword, payload.newPassword);
      yield put(authActions.setPasswordSecurity(isPasswordSecure));
      yield put(authActions.changePasswordSuccess());
      yield call(toast.success, 'La contraseña se modificó con éxito.');
    } else {
      yield put(authActions.changePasswordFailed(message));
      yield call(toast.error, 'Error al intentar cambiar la contraseña.');
    }
  } catch (e) {
    yield put(authActions.changePasswordFailed('Falló el cambio de contraseña'));
    yield call(toast.error, 'Error al intentar cambiar la contraseña.');
  }
}
export function* resetPasswordRequest({ payload }: { payload: Object }) {
  try {
    if (!payload.token) {
      yield put(authActions.resetPasswordFailed('Fallo de seguridad, recargue la página y vuelva a intentarlo'));
      return;
    }

    const dataCaptcha = yield call(User.validateToken, {
      token: payload.token
    });

    if (process.env.REACT_APP_ENV === "prodint" || dataCaptcha.success && dataCaptcha.score >= SECURITY_LIMIT) {
      const data = yield call(User.resetPassword, {
        userName: payload.userName
      });
      if (data.success) {
        const { message } = data;
        yield put(authActions.resetPasswordSuccess());
        yield call(
          toast.success, 
          `Se envió un correo de recuperación a ${message || 'la casilla de correo asociada al usuario.'}`
        );
        yield put(push('/login'));
      } else {
        yield put(authActions.resetPasswordFailed(data.message));
      }
    } else {
      yield put(authActions.resetPasswordFailed('Fallo en validación de seguridad, vuelva a intentarlo mas tarde'));
    }
  } catch (e) {
    yield put(authActions.resetPasswordFailed('Falló el recuperar la contraseña'));
  }
}
export function* impersonateRequest({ payload }) {
  try {
    const { providerCode = null } = payload;
    if (providerCode != null) {
      const data = yield call(User.impersonate, { userName: providerCode });
      if (data.success) {
        const originalToken = localStorage.getItem('ocot_access_token');
        localStorage.setItem('ocot_original_access_token', originalToken);
        yield put(
          authActions.impersonateSuccess({
            user: data.user
          })
        );
        yield call(setAccessToken, data.token);
      } else {
        put(authActions.impersonateFailed('Error message.'));
      }
    }
  } catch (e) {
    // console.log(e);
  }
}
export function* impersonateRollback() {
  try {
    const originalToken = yield call(() => localStorage.getItem('ocot_original_access_token'));
    localStorage.removeItem('ocot_original_access_token');
    const { originalUser } = yield select(state => state.auth);
    yield put(authActions.impersonateRollbackSuccess({ impersonating: false, user: originalUser, originalUser: {} }));
    yield call(setAccessToken, originalToken);
    yield put(push('/admin/pagos'));
  } catch (e) {
    // console.log(e);
  }
}
export default function* authSaga(): Saga {
  yield takeLatest(loginTypes.LOGOUT, logoutSaga);
  yield takeLatest(loginTypes.REQUEST, loginRequestSaga);
  yield takeLatest('persist/REHYDRATE', rehydrateSaga);
  yield takeLatest(userTypes.ACCEPT_TERMS_REQUEST, acceptTermsRequest);
  yield takeLatest(userTypes.CHANGE_PASSWORD_REQUEST, changePasswordRequest);
  yield takeLatest(userTypes.RESET_PASSWORD_REQUEST, resetPasswordRequest);
  yield takeLatest(impersonateTypes.REQUEST, impersonateRequest);
  yield takeLatest(impersonateTypes.ROLLBACK, impersonateRollback);
}
